'use client';

import { Button } from '@repo/design-system/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/design-system/components/ui/select';
import { DatePickerWithRange } from '@repo/design-system/components/ui/date-picker';
import { DatePicker } from '@repo/design-system/components/ui/date-picker';
import { Input } from '@repo/design-system/components/ui/input';
import { format, isValid, parse, subDays, subMonths, subYears } from 'date-fns';
import { useState, useEffect, useCallback } from 'react';
import type { DateRange } from 'react-day-picker';
import { formatLocalDate, parseDateStringAsLocal } from '../../lib/date-filter-utils';

export type DateFilterOperator = 
  | 'is_in_the_last'
  | 'is_equal_to'
  | 'is_between'
  | 'is_on_or_after'
  | 'is_before_or_on'
  | 'is_after'
  | 'is_before';

export interface AdvancedDateFilter {
  operator?: DateFilterOperator;
  value?: string | DateRange;
  relativeValue?: number;
  relativeUnit?: 'days' | 'weeks' | 'months' | 'years';
  
  // Legacy format support
  startDate?: string;
  endDate?: string;
  exact_date?: string;
}

interface AdvancedDateFilterProps {
  value?: AdvancedDateFilter;
  onChange: (value: AdvancedDateFilter | undefined) => void;
  columnName: string;
  onApply?: () => void;
}

const operatorOptions = [
  { value: 'is_in_the_last', label: 'is in the last' },
  { value: 'is_equal_to', label: 'is equal to' },
  { value: 'is_between', label: 'is between' },
  { value: 'is_on_or_after', label: 'is on or after' },
  { value: 'is_before_or_on', label: 'is before or on' },
];

const relativeUnitOptions = [
  { value: 'days', label: 'days' },
  { value: 'weeks', label: 'weeks' },
  { value: 'months', label: 'months' },
  { value: 'years', label: 'years' },
];

export function AdvancedDateFilter({ value, onChange, columnName, onApply }: AdvancedDateFilterProps) {
  // Handle legacy format values (startDate/endDate or exact_date)
  const getInitialOperator = (): DateFilterOperator => {
    if (value?.operator) {
      return value.operator;
    }
    
    // Legacy format detection
    if (value?.startDate && value?.endDate) {
      return 'is_between';
    }
    if (value?.exact_date) {
      return 'is_equal_to';
    }
    if (value?.startDate) {
      return 'is_on_or_after';
    }
    if (value?.endDate) {
      return 'is_before_or_on';
    }
    
    return 'is_in_the_last';
  };

  const getInitialSingleDate = (): Date | undefined => {
    if (value?.value && typeof value.value === 'string') {
      return parseDateStringAsLocal(value.value);
    }
    if (value?.exact_date) {
      return parseDateStringAsLocal(value.exact_date);
    }
    if (value?.startDate) {
      return parseDateStringAsLocal(value.startDate);
    }
    if (value?.endDate) {
      return parseDateStringAsLocal(value.endDate);
    }
    return undefined;
  };

  const getInitialDateRange = (): DateRange | undefined => {
    if (value?.value && typeof value.value === 'object' && 'from' in value.value && 'to' in value.value) {
      return value.value as DateRange;
    }
    if (value?.startDate && value?.endDate) {
      return {
        from: parseDateStringAsLocal(value.startDate),
        to: parseDateStringAsLocal(value.endDate),
      };
    }
    return undefined;
  };

  const [operator, setOperator] = useState<DateFilterOperator>(getInitialOperator());
  const [relativeValue, setRelativeValue] = useState<number>(value?.relativeValue || 30);
  const [relativeUnit, setRelativeUnit] = useState<'days' | 'weeks' | 'months' | 'years'>(value?.relativeUnit || 'days');
  const [singleDate, setSingleDate] = useState<Date | undefined>(getInitialSingleDate());
  const [dateRange, setDateRange] = useState<DateRange | undefined>(getInitialDateRange());

  // Sync internal state when value changes externally (e.g. "Clear All Filters")
  useEffect(() => {
    setOperator(getInitialOperator());
    setRelativeValue(value?.relativeValue ?? 30);
    setRelativeUnit(value?.relativeUnit ?? 'days');
    setSingleDate(getInitialSingleDate());
    setDateRange(getInitialDateRange());
  }, [value]);

  // Helper function to create filter value
  const createFilterValue = useCallback((): AdvancedDateFilter | undefined => {
    if (operator === 'is_in_the_last') {
      if (relativeValue && relativeUnit && relativeValue > 0) {
        return {
          operator,
          relativeValue,
          relativeUnit,
        };
      }
    } else if (operator === 'is_between') {
      if (dateRange?.from && dateRange?.to) {
        return {
          operator,
          value: dateRange,
        };
      }
    } else if (['is_equal_to', 'is_on_or_after', 'is_before_or_on', 'is_after', 'is_before'].includes(operator)) {
      if (singleDate) {
        return {
          operator,
          value: formatLocalDate(singleDate),
        };
      }
    }
    return undefined;
  }, [operator, relativeValue, relativeUnit, singleDate, dateRange]);

  // Helper function to update filter
  const updateFilter = useCallback((newValue: AdvancedDateFilter | undefined) => {
    const currentValueString = JSON.stringify(newValue);
    const previousValueString = JSON.stringify(value);
    
    if (currentValueString !== previousValueString) {
      onChange(newValue);
    }
  }, [value, onChange]);

  // Remove automatic filter updates - now only apply when user clicks Apply button
  // useEffect(() => {
  //   // Don't trigger filter on initial load
  //   if (!hasUserInteracted) {
  //     return;
  //   }

  //   const newValue = createFilterValue();
    
  //   // Only update if we have a valid filter value (user has made a selection)
  //   if (newValue) {
  //     updateFilter(newValue);
  //   }
  // }, [createFilterValue, updateFilter, hasUserInteracted]);

  const handleOperatorChange = (newOperator: string) => {
    setOperator(newOperator as DateFilterOperator);
  };

  const handleRelativeValueChange = (newValue: number) => {
    setRelativeValue(newValue);
  };

  const handleRelativeUnitChange = (newUnit: string) => {
    setRelativeUnit(newUnit as 'days' | 'weeks' | 'months' | 'years');
  };

  const handleSingleDateChange = (newDate: Date | undefined) => {
    setSingleDate(newDate);
  };

  const handleDateRangeChange = (newRange: DateRange | undefined) => {
    setDateRange(newRange);
  };

  const renderValueInput = () => {
    switch (operator) {
      case 'is_in_the_last':
        return (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="1"
              value={relativeValue}
              onChange={(e) => handleRelativeValueChange(Number.parseInt(e.target.value) || 1)}
              className="w-20"
            />
            <Select value={relativeUnit} onValueChange={handleRelativeUnitChange}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" side="bottom" align="start" className="w-[var(--radix-select-trigger-width)]">
                {relativeUnitOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'is_between':
        return (
          <DatePickerWithRange
            selectedDate={dateRange}
            setSelectedDate={(newValue) => {
              if (typeof newValue === 'function') {
                handleDateRangeChange(newValue(dateRange));
              } else {
                handleDateRangeChange(newValue);
              }
            }}
            allowPastDates={true}
          />
        );

      case 'is_equal_to':
      case 'is_on_or_after':
      case 'is_before_or_on':
        return (
          <DatePicker
            selectedDate={singleDate}
            setSelectedDate={(newValue) => {
              if (typeof newValue === 'function') {
                handleSingleDateChange(newValue(singleDate));
              } else {
                handleSingleDateChange(newValue);
              }
            }}
            calendarProps={{ disabled: { after: new Date() } }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-3 text-sm">
      <strong>Filter by {columnName}</strong>
      
      <div className="flex flex-col gap-2">
        <Select value={operator || 'is_in_the_last'} onValueChange={handleOperatorChange}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent position="popper" side="bottom" align="start" className="w-[var(--radix-select-trigger-width)]">
            {operatorOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {renderValueInput()}
      </div>

      <div className="flex gap-2 pt-2 border-t">
        <Button 
          size="sm" 
          className="flex-1"
          onClick={() => {
            const newValue = createFilterValue();
            if (newValue) {
              onChange(newValue);
            }
            onApply?.();
          }}
        >
          Apply
        </Button>
        <Button 
          size="sm" 
          variant="outline" 
          className="flex-1"
          onClick={() => {
            onChange(undefined);
            onApply?.();
          }}
        >
          Clear
        </Button>
      </div>
    </div>
  );
}
